import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import {
  Alert,
  RadioButton,
  TextInput,
  Modal,
  LinkButton,
  Tooltip,
} from '@pmi/dsm-react';
import IconDanger from '@pmi/dsm-react/dist/components/icons/16/CircleRemoveIcon';
import { BaseAdmin } from '../models';
import { accountSelector } from '../../../../../shared/store/shared/accountSlice';
import { toastWithAnalytics } from '../../../../../shared/analytics';
import { useInviteAdminMutation, useUpdateAdminMutation } from './apiSlice';
import usePermissions from '../../../../../shared/hooks/usePermissions';
import Permissions from '../../../../../shared/permissions';
import styles from './InviteEditAdminModal.module.scss';
import EditPhoneNumberModal from './EditAdminPhoneNumberModal';
import DeleteAdmin2FANumberModal from './DeleteAdminPhoneNumberModal';
import { useGetAdminPhoneNumberFieldSchemaQuery } from './EditAdminPhoneNumberModal/apiSlice';
import {
  normalizeEmail,
  singleEmailRegex,
} from '../../../../../shared/helpers';

interface InviteEditAdminModalProps {
  admin: BaseAdmin;
  onCancel?: () => void;
}

export default function InviteEditAdminModal({
  admin,
  onCancel = () => {},
}: InviteEditAdminModalProps) {
  if (!admin) return null;
  const { id: adminId = 0, role, isOwnAccount } = admin;
  const { currentAccountId } = useSelector(accountSelector);
  const [selectedRoleId, setSelectedRoleId] = useState(
    adminId ? role : 'B2B:ADM',
  );
  const [initRole, setInitRole] = useState('');
  const [email, setEmail] = useState(
    admin.email || admin.invitationEmail || '',
  );
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [roleError, setRoleError] = useState(false);
  const [updateAdmin, updatedAdmin] = useUpdateAdminMutation();
  const [inviteAdmin, invitedAdmin] = useInviteAdminMutation();
  const { hasAccountPermissions, hasAccess } = usePermissions();
  const {
    data: phoneNumberFormHelper,
    isLoading: schemaLoading,
    isFetching: schemaFetching,
  } = useGetAdminPhoneNumberFieldSchemaQuery();

  const [showEditPhoneNumberModal, setShowEditPhoneNumberModal] =
    useState(false);
  const [showDeletePhoneNumberModal, setShowDeletePhoneNumberModal] =
    useState(false);

  const onRoleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedRoleId(event.target.value);
    setRoleError(false);
  };

  const onSubmitDsmCompatible = async () => {
    // Validate email
    const isValid = !!adminId || singleEmailRegex.test(normalizeEmail(email));
    setIsEmailValid(isValid);
    if (!isValid) return;
    // Validate selected role
    if (!selectedRoleId) {
      setRoleError(true);
      return;
    }
    const inviteOrUpdate =
      adminId || admin.inviteCode ? updateAdmin : inviteAdmin;
    const inviteOrUpdated =
      adminId || admin.inviteCode ? updatedAdmin : invitedAdmin;
    // Call API
    inviteOrUpdate({
      accountId: currentAccountId,
      data: {
        email: normalizeEmail(email),
        invitationEmail: admin.invitationEmail,
        adminId,
        accountRole: selectedRoleId,
        inviteCode: admin.inviteCode,
      },
    })
      .unwrap()
      .then(() => {
        const message = (): ReactNode => (
          <>
            {`${adminId ? 'You have updated' : 'Invite sent to'}`}{' '}
            <span data-exclude-lg="true">{email}</span>
          </>
        );
        toastWithAnalytics(message(), 'success');
        onCancel();
        inviteOrUpdated.reset();
      })
      .catch((err) => {
        try {
          const errorValue = JSON.parse(err?.data?.Value);
          if (errorValue.detail && errorValue.detail.includes('already')) {
            toastWithAnalytics(
              'The email address you entered is already associated with a current admin. Please try again.',
              'danger',
            );
            return;
          }
        } catch (e) {}
        toastWithAnalytics(
          `${
            adminId ? 'User could not be updated' : 'Invite could not be sent'
          }. Please try again.`,
          'danger',
        );
      });
  };

  const onSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmitDsmCompatible();
  };
  // eslint-disable-next-line no-unused-vars
  const onCancelHandler = (e?: any) => {
    onCancel();
    updatedAdmin.reset();
    invitedAdmin.reset();
  };

  const loading = updatedAdmin.isLoading || invitedAdmin.isLoading;

  const roleHasPermission = useMemo(
    () =>
      hasAccountPermissions() &&
      hasAccess({
        permissions: [Permissions.InviteAdminOrReporter],
      }),
    [admin],
  );

  useEffect(() => {
    setInitRole(adminId ? role : '');
    setSelectedRoleId(role ?? 'B2B:ADM');
  }, []);

  return (
    <>
      <Modal
        show
        customClassName={styles.inviteEditModal}
        onClose={onCancelHandler}
        variant="input"
        title={adminId ? 'Edit' : 'Invite'}
        buttons={[
          {
            titleText: 'Cancel',
            variant: 'outline-secondary',
            onClick: onCancelHandler,
            disabled: loading,
          },
          {
            titleText: adminId ? 'Update' : 'Invite',
            variant: 'primary',
            onClick: onSubmitDsmCompatible,
            disabled: loading || initRole === selectedRoleId,
          },
        ]}
      >
        <div className="text-start">
          <form onSubmit={onSubmit} data-testid="invite-form">
            <Form.Group
              controlId="email"
              className={isEmailValid ? '' : 'is-invalid'}
            >
              <Form.Label>Email Address</Form.Label>
              <TextInput
                className={isEmailValid ? '' : 'is-invalid'}
                validationMessage="Please enter a valid email address."
                isInvalid={!isEmailValid}
                value={email}
                onChange={(e: FormEvent<HTMLInputElement>) =>
                  setEmail(e.currentTarget?.value)
                }
                id="email"
                data-testid="email-input"
                name="email"
                disabled={!!adminId || loading}
              />
            </Form.Group>
          </form>

          {adminId > 0 && (
            <>
              <Form.Label className="mt-3">Phone Number</Form.Label>
              <div className="d-flex align-items-center">
                {admin.phoneNumber && (
                  <div className="me-2 m-0" data-exclude-lg>
                    {admin.phoneNumber}
                  </div>
                )}
                <LinkButton
                  className={`${styles.prodLink} p-0 m-0`}
                  variant="light"
                  disabled={schemaLoading || schemaFetching}
                  titleText={`${
                    admin.phoneNumber ? 'Edit' : '+ Add Phone number'
                  }`}
                  onClick={(e: any) => {
                    e.preventDefault();
                    if (schemaLoading || schemaFetching) return;
                    setShowEditPhoneNumberModal(true);
                  }}
                />
                {admin.phoneNumber && (
                  <LinkButton
                    className={`${styles.deleteLink} p-0 ms-2 color-danger`}
                    variant="light"
                    titleText="Remove"
                    onClick={(e: any) => {
                      e.preventDefault();
                      setShowDeletePhoneNumberModal(true);
                    }}
                  />
                )}
              </div>
            </>
          )}

          <div>
            <Form.Label className="mb-2 mt-3">Select Role:</Form.Label>
            {roleHasPermission && (
              <div className="border rounded p-3 mb-4">
                <Tooltip
                  type="sm"
                  content="You do not have permission to change your role"
                  placement="top-start"
                  blockTriggers={!isOwnAccount}
                  className="darkTooltip"
                >
                  <RadioButton
                    onChange={onRoleChange}
                    inputProps={{
                      'aria-label': 'Administrator',
                      'data-testid': 'administrator-role-radio',
                    }}
                    name="radio-buttons-roles"
                    value="B2B:ADM"
                    label={
                      <div className="ms-2">
                        <div
                          className={`body3 fw-bold text-link text-link--primary text-start ${styles.textLeftAlign}`}
                        >
                          Administrator
                        </div>
                        <div className="body4">
                          Users with this role can view and modify all account
                          settings and info.
                        </div>
                      </div>
                    }
                    id="administrator-role-radio"
                    checked={selectedRoleId === 'B2B:ADM'}
                    disabled={loading || isOwnAccount}
                  />
                </Tooltip>
              </div>
            )}

            {roleHasPermission && (
              <div className="border rounded p-3 mb-4">
                <Tooltip
                  type="sm"
                  content="You do not have permission to change your role"
                  placement="top-start"
                  blockTriggers={!isOwnAccount}
                  className="darkTooltip"
                >
                  <RadioButton
                    onChange={onRoleChange}
                    inputProps={{
                      'aria-label': 'Reporter',
                      'data-testid': 'reporter-role-radio',
                    }}
                    name="radio-buttons-roles"
                    value="B2B:RPT"
                    label={
                      <div className="ms-2">
                        <div
                          className={`body3 fw-bold text-link text-link--primary text-start ${styles.textLeftAlign}`}
                        >
                          Reporter
                        </div>
                        <div className="body4">
                          Users with this read-only role can view account
                          information only.
                        </div>
                      </div>
                    }
                    id="reporter-role-radio"
                    checked={selectedRoleId === 'B2B:RPT'}
                    disabled={loading || isOwnAccount}
                  />
                </Tooltip>
              </div>
            )}
            {roleError && (
              <Alert
                variant="danger"
                alertText="Please select role to proceed"
                icon={IconDanger}
              />
            )}
          </div>
        </div>
      </Modal>

      {showEditPhoneNumberModal && (
        <EditPhoneNumberModal
          id={admin.id}
          countries={phoneNumberFormHelper?.countries}
          phoneNumberSchema={phoneNumberFormHelper?.schema}
          phoneNumber={admin.phone}
          onCancel={() => setShowEditPhoneNumberModal(false)}
        />
      )}

      {showDeletePhoneNumberModal && (
        <DeleteAdmin2FANumberModal
          id={admin.id}
          onSuccess={() => setShowDeletePhoneNumberModal(false)}
          onCancel={() => setShowDeletePhoneNumberModal(false)}
        />
      )}
    </>
  );
}
